import { Space, Switch, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";

import type { ColumnsType } from "antd/es/table";
import { ICdicCoupon, ICouponProgram } from "@interfaces";
import { getApiErrorMessage, theme, useNotify, useUpdateCdicCoupon } from "@utils";

import { memo, useEffect } from "react";
import { Link } from "react-router-dom";
import { PATH_CDIC_COUPON_LIST, dateFormat, enumStatus } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";
import dayjs from "dayjs";

interface IProps {
  data: ICouponProgram[];
  callback: () => void;
}
export const CouponProgramTable = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();
  const updateCdicCoupon = useUpdateCdicCoupon();

  useEffect(() => {
    dispatch(setLoading(updateCdicCoupon.isPending));
  }, [dispatch, updateCdicCoupon.isPending]);

  const dataSource: ICouponProgram[] = data;
  const color = (status: enumStatus) => {
    switch (status) {
      case enumStatus.ACTIVE:
        return theme.colors.active;
        break;
      case enumStatus.INACTIVE:
        return theme.colors.expired;
        break;
      case enumStatus.REDEEMED:
        return theme.colors.main;
        break;
      case enumStatus.REJECT:
        return theme.colors.reject;
        break;
      default:
        return theme.colors.expired;
        break;
    }
  };
  const getStatus = (status: enumStatus) => {
    switch (status) {
      case enumStatus.ACTIVE:
        return t("page.active");
        break;
      case enumStatus.INACTIVE:
        return t("page.inactive");
        break;
      case enumStatus.REJECT:
        return t("page.rejected");
        break;
      case enumStatus.REDEEMED:
        return t("page.redeemed");
        break;
      case enumStatus.EXPIRED:
        return t("page.expired");
        break;
      default:
        return status;
        break;
    }
  };
  const columns: ColumnsType<ICouponProgram> = [
    {
      title: t("page.program_name"),
      dataIndex: "programName",
      key: "1",
      render: (text, record) => (
        <span>
          <Link to={PATH_CDIC_COUPON_LIST + `/${record.programId}`}>{record?.programName}</Link>
        </span>
      ),
      sorter: (a, b) => {
        if (a?.programName && b?.programName) {
          return a?.programName.localeCompare(b?.programName);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.no_of_coupons"),
      dataIndex: "noOfCoupons",
      key: "2",
      render: (text, record) => <span>{record?.noOfCoupons}</span>,
      sorter: (a, b) => {
        if (a?.noOfCoupons && b?.noOfCoupons) {
          return a?.noOfCoupons - b?.noOfCoupons;
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.created_at"),
      dataIndex: "createdAt",
      key: "4",
      render: (data) => <span>{dayjs(data).format(dateFormat)}</span>,
      sorter: (a, b) => {
        if (a?.createdAt && b?.createdAt) {
          return dayjs(a?.createdAt).unix() - dayjs(b?.createdAt).unix();
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.updated_at"),
      dataIndex: "updatedAt",
      key: "5",
      render: (data) => <span>{dayjs(data).format(dateFormat)}</span>,
      sorter: (a, b) => {
        if (a?.updatedAt && b?.updatedAt) {
          return dayjs(a?.updatedAt).unix() - dayjs(b?.updatedAt).unix();
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.available_coupons"),
      dataIndex: "availableCoupons",
      key: "3",
      render: (tag) => (
        <Tag color={"#00AA67"} key={tag}>
          {tag}
        </Tag>
      ),
    },
    {
      title: t("page.actions"),
      key: "6",
      render: (_, record) => (
        <Space size="middle">
          <Switch
            checked={record?.status === enumStatus.ACTIVE}
            onChange={(value: boolean) => onChange(value, record as unknown as ICdicCoupon)}
          />
        </Space>
      ),
    },
  ];

  const onChange = async (value: boolean, record: ICdicCoupon) => {
    const updateStatus = value ? enumStatus.ACTIVE : enumStatus.INACTIVE;
    try {
      await updateCdicCoupon.mutateAsync({
        payload: {
          ...record,
          status: updateStatus,
        },
        id: record._id || "",
      });
      callback();
      success(t("message.update.success"));
    } catch (err) {
      error(getApiErrorMessage(err, t("message.update.fail")));
    }
  };

  return (
    <Table dataSource={dataSource} rowKey={"couponFamilyId"} columns={columns} pagination={false} />
  );
});
